import { StyledTable } from '@/Components/Elements/StyledComponents/Tables'
import React from 'react'
import {
    Alert,
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Paper,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import DefaultSearchbar from '@/Components/Elements/Search/DefaultSearchbar'
import {
    FormInputLabel,
    selectionControlLabelProps,
    selectionControlSx,
} from '@/Components/Elements/StyledComponents/Input'
import { ExportFieldsTableProps } from '../types'

export default function ExportFieldsTable(props: ExportFieldsTableProps) {
    const {
        kedo,
        fields,
        metaFields,
        selectedFields,
        loading,
        fileType,
        removeHtml,
        onToggleRemoveHtml,
        selectAllFields,
        searchValue,
        handleFilterChange,
        handleSearchFields,
        resetButtonFields,
        renderItemsId,
        getItemMode,
        onChangeFields,
    } = props

    const visibleFields = [...(fields || []), ...(metaFields || [])]
    const selectedIds = new Set(selectedFields.map((field) => String(field.id)))
    const selectedVisibleCount = visibleFields.filter((field) =>
        selectedIds.has(String(field.id))
    ).length
    const allSelected =
        visibleFields.length > 0 &&
        selectedVisibleCount === visibleFields.length

    return (
        <FormGroup className="table-responsive">
            {fileType === 'csv' && onToggleRemoveHtml && (
                <FormControlLabel
                    {...selectionControlLabelProps}
                    control={
                        <Checkbox
                            size="small"
                            checked={removeHtml}
                            name="removeHtml"
                            onChange={(e) =>
                                onToggleRemoveHtml(e.target.checked)
                            }
                            sx={(theme) => selectionControlSx(theme)}
                        />
                    }
                    label={kedo.t(
                        'Would you like the HTML tags to be stripped from the CSV file?'
                    )}
                />
            )}

            <FormInputLabel sx={{ mt: 1 }}>
                <h5>{kedo.t('Fields to export')}</h5>
            </FormInputLabel>

            <Box sx={{ mb: 2 }}>
                <DefaultSearchbar
                    kedo={kedo}
                    handleChange={handleFilterChange}
                    onSearch={handleSearchFields}
                    searchName={searchValue}
                    onReset={resetButtonFields}
                />
            </Box>

            {!loading && visibleFields.length === 0 && (
                <Alert severity="warning" variant="outlined">
                    {kedo.t('No fields found')}
                </Alert>
            )}

            {loading ? (
                <LoadingDefault />
            ) : (
                <TableContainer component={Paper}>
                    <StyledTable size="small" striped>
                        <TableHead>
                            <TableRow
                                sx={{
                                    '& .MuiTableCell-head': {
                                        textAlign: 'left',
                                        fontWeight: 'bold',
                                    },
                                }}
                            >
                                <TableCell>
                                    <FormControlLabel
                                        {...selectionControlLabelProps}
                                        sx={{
                                            ...selectionControlLabelProps.sx,
                                            width: '100%',
                                            mr: 0,
                                        }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={allSelected}
                                                disabled={
                                                    visibleFields.length === 0
                                                }
                                                indeterminate={
                                                    selectedVisibleCount > 0 &&
                                                    !allSelected
                                                }
                                                onChange={selectAllFields}
                                                sx={(theme) =>
                                                    selectionControlSx(theme)
                                                }
                                            />
                                        }
                                        label={
                                            <strong>
                                                {kedo.t('Select all')}
                                            </strong>
                                        }
                                    />
                                </TableCell>
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(fields || []).map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <FormControlLabel
                                            {...selectionControlLabelProps}
                                            sx={{
                                                ...selectionControlLabelProps.sx,
                                                width: '100%',
                                                mr: 0,
                                            }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={
                                                        !!selectedFields.find(
                                                            (f) =>
                                                                String(f.id) ===
                                                                String(item.id)
                                                        )
                                                    }
                                                    onChange={onChangeFields}
                                                    value={item.id}
                                                    sx={(theme) =>
                                                        selectionControlSx(
                                                            theme
                                                        )
                                                    }
                                                />
                                            }
                                            label={kedo.translateItem(
                                                item,
                                                'displayitem'
                                            )}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {item.def_dossier_link &&
                                            renderItemsId?.(item.id)}
                                    </TableCell>
                                    <TableCell>
                                        <small>{getItemMode?.(item)}</small>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {(metaFields || []).map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <FormControlLabel
                                            {...selectionControlLabelProps}
                                            sx={{
                                                ...selectionControlLabelProps.sx,
                                                width: '100%',
                                                mr: 0,
                                            }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={
                                                        !!selectedFields.find(
                                                            (f) =>
                                                                String(f.id) ===
                                                                String(item.id)
                                                        )
                                                    }
                                                    onChange={onChangeFields}
                                                    value={item.id}
                                                    sx={(theme) =>
                                                        selectionControlSx(
                                                            theme
                                                        )
                                                    }
                                                />
                                            }
                                            label={item.label}
                                        />
                                    </TableCell>
                                    <TableCell />
                                    <TableCell>
                                        <small>Metadata</small>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </StyledTable>
                </TableContainer>
            )}
        </FormGroup>
    )
}
